<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef } from 'vue';
import { ToastRoot } from 'reka-ui';
import Alert from '../alert/alert.vue';
import { AlertType } from '../alert/alert.constants';
import type { Toast } from './toast.types';
import { useToast } from './use-toast';

type Props = {
  toast: Toast;
};

const props = defineProps<Props>();

const { remove } = useToast();

const root = useTemplateRef('root');
const height = ref(0);

onMounted(async () => {
  await nextTick();
  const element = root.value?.$el;
  if (element instanceof HTMLElement) {
    height.value = element.getBoundingClientRect().height;
  }
});

defineExpose({ id: props.toast.id, height });

const onOpenChange = (isOpen: boolean): void => {
  if (!isOpen) remove(props.toast.id);
};

const liveRegionType: Record<AlertType, 'foreground' | 'background'> = {
  [AlertType.INFO]: 'background',
  [AlertType.SUCCESS]: 'background',
  [AlertType.WARNING]: 'foreground',
  [AlertType.ERROR]: 'foreground',
};
</script>

<template>
  <ToastRoot
    ref="root"
    :open="props.toast.open"
    :duration="props.toast.duration"
    :type="liveRegionType[props.toast.type]"
    @update:open="onOpenChange"
  >
    <Alert :type="props.toast.type" icon class="h-full min-h-14">
      <component
        :is="props.toast.component"
        v-if="props.toast.component"
        v-bind="props.toast.props"
      />
      <template v-else>
        <p v-if="props.toast.title" class="text-label">
          {{ props.toast.title }}
        </p>
        <p v-if="props.toast.description" class="text-body-md">
          {{ props.toast.description }}
        </p>
      </template>
    </Alert>
  </ToastRoot>
</template>
